import React, { useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import { RangeFilter } from "../Ranges/RangeFilter";
import { FilterRange } from "../Ranges/types";
import { useRangeFilter } from "../Ranges/hooks/useRangeFilter";
import { FilterLayout } from "./FilterLayout";

export const EmployeeRangeFilter = React.memo(() => {
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();

  // Get initial values from draftFilters
  const initialRange = useMemo<FilterRange>(
    () =>
      ({
        min: draftFilters?.employees?.min,
        max: draftFilters?.employees?.max,
      }) as FilterRange,
    [draftFilters?.employees?.min, draftFilters?.employees?.max]
  );

  // Handle employee range change
  const handleEmployeeRangeChange = useCallback(
    (newRange: FilterRange) => {
      setDraftFilters((prevFilters) => {
        if (newRange.min === undefined && newRange.max === undefined) {
          return {
            ...prevFilters,
            employees: undefined,
            hasFixedEmployees: undefined,
          };
        }

        return {
          ...prevFilters,
          employees: {
            min: newRange.min,
            max: newRange.max,
          },
        };
      });
    },
    [setDraftFilters]
  );

  // Handle fixed employees change
  const handleFixedEmployeesChange = useCallback(
    (hasFixed: boolean) => {
      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        hasFixedEmployees: hasFixed || undefined,
      }));
    },
    [setDraftFilters]
  );

  // Use the range filter hook
  const {
    range,
    isFixed: hasFixedEmployees,
    rangeHasValues,
    handleRangeChange,
    handleFixedValueChange: handleFixedEmployeesCheckChange,
    handleClearFilter,
  } = useRangeFilter({
    initialRange,
    hasFixedValue: !!draftFilters?.hasFixedEmployees,
    onRangeChange: handleEmployeeRangeChange,
    onFixedValueChange: handleFixedEmployeesChange,
  });

  return (
    <FilterLayout
      title="Employee Range"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      id="employee-range-filter"
      refProp={elementRef}
    >
      <RangeFilter
        value={range}
        onChange={(min, max) => handleRangeChange({ min, max })}
      />
      <Stack direction="row" my={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasFixedEmployees}
              onChange={handleFixedEmployeesCheckChange}
              disabled={!rangeHasValues}
            />
          }
          label="Limit to companies with fixed employee values within this range"
        />
      </Stack>
    </FilterLayout>
  );
});
